import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar, Legend } from 'recharts';
import { useLanguage } from '../../i18n/LanguageContext';


const backendUrl = 'http://localhost:3001/api';

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <h3 style={{ marginTop: 0 }}>{title}</h3>
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
  </div>
);

const Sparkline: React.FC<{ data: { date: string; value: number }[] }> = ({ data }) => {
  const points = data.map((d, i) => `${i * 12},${40 - (d.value || 0)}`).join(' ');
  return (
    <svg width={data.length * 12} height={40} style={{ background: '#fafafa' }}>
      <polyline fill="none" stroke="#1976d2" strokeWidth="2" points={points} />
    </svg>
  );
};

const BankDashboard: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [np30, setNp30] = useState<any>(null);
  const [np60, setNp60] = useState<any>(null);
  const [np90, setNp90] = useState<any>(null);
  const [merchantId, setMerchantId] = useState('');
  const [signals, setSignals] = useState<any>(null);
  const [series, setSeries] = useState<{ loanActivities: any[]; loans: any[] }>({ loanActivities: [], loans: [] });
  const [top, setTop] = useState<any[]>([]);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${backendUrl}/metrics/summary`).then(r => setSummary(r.data)).catch(console.error);
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 30 } }).then(r => setNp30(r.data)).catch(console.error);
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 60 } }).then(r => setNp60(r.data)).catch(console.error);
    axios.get(`${backendUrl}/metrics/nonperforming`, { params: { bucket: 90 } }).then(r => setNp90(r.data)).catch(console.error);
    axios.get(`${backendUrl}/metrics/timeseries`).then(r => setSeries(r.data || { loanActivities: [], loans: [] })).catch(console.error);
    axios.get(`${backendUrl}/metrics/top-merchants`, { params: { limit: 5 } }).then(r => setTop(r.data || [])).catch(console.error);
  }, []);

  const fetchSignal = () => {
    if (!merchantId) return setSignals(null);
    axios.get(`${backendUrl}/metrics/early-signals`, { params: { merchantId } }).then(r => setSignals(r.data));
  };

  return (
    <div>
      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>{t('bank.dashboard.title')}</h1>
          <p style={{ color: '#64748b', fontSize: 16 }}>{t('bank.dashboard.subtitle')}</p>
        </div>

      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 16, 
        marginBottom: 24,
        alignItems: 'stretch'
      }}>
        <KpiCard 
          title={t('kpi.totalDisbursed')} 
          value={summary ? `${Number(summary.totalDisbursed || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#1565c0" 
          deltaPct={summary?.deltas?.disbursed} 
          subtitle={t('kpi.vsPrevPeriod')}
          clickable={true}
          details={{
            type: 'disbursements',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.disbursementAmount || 0
            })) || [],
            summary: {
              totalDisbursed: summary?.totalDisbursed,
              avgDisbursed: summary?.totalDisbursed && summary?.totalLoans ? summary.totalDisbursed / summary.totalLoans : 0,
              totalLoans: summary?.totalLoans
            }
          }}
        />
        <KpiCard 
          title={t('kpi.totalRepaid')} 
          value={summary ? `${Number(summary.totalRepaid || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#12b569"
          clickable={true}
          details={{
            type: 'repayments',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.repaymentAmount || 0
            })) || [],
            summary: {
              totalRepaid: summary?.totalRepaid,
              repaymentRate: summary?.repaymentRate,
              onTimePayments: summary?.onTimePayments
            }
          }}
        />
        <KpiCard 
          title={t('kpi.loanApplications')} 
          value={summary ? summary.totalApplications || 0 : '—'} 
          color="#1976d2" 
          deltaPct={summary?.deltas?.applications} 
          subtitle={t('kpi.vsPrevPeriod')}
          clickable={true}
          details={{
            type: 'applications',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              submissionDate: v.date,
              requestedAmount: v.disbursementAmount || 0
            })) || [],
            summary: {
              totalApplications: summary?.totalApplications,
              pendingApplications: summary?.pendingApplications,
              avgApplicationAmount: summary?.avgApplicationAmount
            }
          }}
        />
        <KpiCard 
          title={t('kpi.rejected')} 
          value={summary ? summary.rejectedLoans : '—'} 
          color="#ff4d4f"
          clickable={true}
          details={{
            type: 'rejected',
            data: [], // Will be populated with actual rejected applications data
            summary: {
              rejectedLoans: summary?.rejectedLoans,
              rejectionRate: summary?.rejectionRate,
              topRejectionReason: 'Credit Score'
            }
          }}
        />
        <KpiCard 
          title={t('kpi.approved')} 
          value={summary ? summary.approvedLoans : '—'} 
          color="#00bcd4"
          clickable={true}
          details={{
            type: 'approved',
            data: [], // Will be populated with actual approved applications data
            summary: {
              approvedLoans: summary?.approvedLoans,
              totalApprovedAmount: summary?.totalApprovedAmount,
              approvalRate: summary?.approvalRate
            }
          }}
        />
        <KpiCard 
          title={t('kpi.overdue')} 
          value={np30 ? np30.count : '—'} 
          color="#f59e0b"
          clickable={true}
          details={{
            type: 'overdue',
            data: [], // Will be populated with actual overdue loans data
            summary: {
              overdue30Days: np30?.count,
              totalOverdueAmount: summary?.totalOverdueAmount,
              riskLevel: 'High'
            }
          }}
        />
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>{t('chart.dailyLoanActivities')}</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={(series.loanActivities || []).map((v: any) => ({ date: v.date, disbursements: v.disbursementAmount || 0, repayments: v.repaymentAmount || 0, applications: v.applicationsCount || 0 }))} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorVol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorLoans" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }} 
              />
              <Legend />
              <Area type="monotone" dataKey="disbursements" stroke="#1565c0" strokeWidth={3} fillOpacity={1} fill="url(#colorVol)" name={t('chart.disbursements')} />
              <Area type="monotone" dataKey="repayments" stroke="#12b569" strokeWidth={3} fillOpacity={1} fill="url(#colorLoans)" name={t('chart.repayments')} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #fef7f0 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #fed7aa',
        boxShadow: '0 4px 20px rgba(251, 146, 60, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>{t('chart.nonPerformingLoans')}</h3>
          <Link 
            to="/bank/np-loans"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
            }}
          >
            {t('bank.viewDetails')} →
          </Link>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fef2f2, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fecaca',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ef4444', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, marginBottom: 4 }}>30+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#dc2626' }}>{np30 ? np30.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 4 }}>Critical Risk</div>
          </div>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fef3f2, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fed7d7',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f97316', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#c2410c', fontWeight: 600, marginBottom: 4 }}>60+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#ea580c' }}>{np60 ? np60.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#9a3412', marginTop: 4 }}>High Risk</div>
          </div>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, #fffbeb, #ffffff)',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fde68a',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f59e0b', borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>90+ DAYS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#d97706' }}>{np90 ? np90.count : '—'}</div>
            <div style={{ fontSize: 11, color: '#78350f', marginTop: 4 }}>Severe Risk</div>
          </div>
        </div>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #bae6fd',
        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>{t('chart.topMerchants')}</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={top || []} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }} 
              />
              <Legend />
              <Bar 
                dataKey="totalDisbursed" 
                fill="url(#volumeGradient)" 
                name={t('chart.disbursements')} 
                onClick={(data: any) => window.location.href = `/bank/merchant/${data.id}`}
                style={{ cursor: 'pointer' }}
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="totalRepaid" 
                fill="url(#txnGradient)" 
                name={t('chart.repayments')}
                radius={[4, 4, 0, 0]}
              />
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0.7}/>
                </linearGradient>
                <linearGradient id="txnGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #d1d5db',
        boxShadow: '0 4px 20px rgba(107, 114, 128, 0.1)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>{t('bank.earlyWarning')}</h3>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
                      <input 
              placeholder={t('bank.enterMerchantId')} 
              value={merchantId} 
              onChange={(e) => setMerchantId(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: 14,
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          />
          <button 
            onClick={fetchSignal}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: 'white',
              padding: '12px 20px',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
            }}
          >
            {t('bank.analyze')}
          </button>
        </div>
        {signals && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{
              flex: '1 1 200px',
              background: 'linear-gradient(135deg, #f0f9ff, #ffffff)',
              borderRadius: 12,
              padding: 16,
              border: '1px solid #bae6fd'
            }}>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 4 }}>PREVIOUS 7 DAYS</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0284c7' }}>{Math.round(signals.prev7Avg || 0).toLocaleString('en-ZW')} ZiG</div>
            </div>
            <div style={{
              flex: '1 1 200px',
              background: 'linear-gradient(135deg, #f0fdf4, #ffffff)',
              borderRadius: 12,
              padding: 16,
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 4 }}>LAST 7 DAYS</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d' }}>{Math.round(signals.last7Avg || 0).toLocaleString('en-ZW')} ZiG</div>
            </div>
            <div style={{
              flex: '1 1 200px',
              background: signals.signal === 'alert' ? 'linear-gradient(135deg, #fef2f2, #ffffff)' : 'linear-gradient(135deg, #f0fdf4, #ffffff)',
              borderRadius: 12,
              padding: 16,
              border: signals.signal === 'alert' ? '1px solid #fecaca' : '1px solid #bbf7d0'
            }}>
              <div style={{ fontSize: 12, color: signals.signal === 'alert' ? '#991b1b' : '#166534', fontWeight: 600, marginBottom: 4 }}>
                {signals.signal === 'alert' ? '⚠️ ALERT STATUS' : '✅ NORMAL STATUS'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: signals.signal === 'alert' ? '#dc2626' : '#15803d' }}>
                {Math.round((signals.drop || 0) * 100)}% Drop
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)', 
        borderRadius: 16, 
        padding: 24, 
        marginBottom: 24,
        border: '1px solid #e9d5ff',
        boxShadow: '0 4px 20px rgba(139, 92, 246, 0.1)'
      }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>System Management</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Link 
            to="/bank/credit-bureau"
            style={{
              flex: '1 1 200px',
              background: 'linear-gradient(135deg, #059669, #047857)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: 12,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            📋 Credit Bureau
          </Link>
          <Link 
            to="/rules"
            style={{
              flex: '1 1 200px',
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: 12,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            ⚙️ Manage Scoring Rules
          </Link>
          <Link 
            to="/audit-log"
            style={{
              flex: '1 1 200px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: 12,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            📊 View Audit Logs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BankDashboard;

